/* eslint-env browser */
// Content that eases in the first time it scrolls into view (landing page):
// opacity and a short rise, 700 ms on a strong ease-out, once. With Reduce
// Motion, or without IntersectionObserver, it is simply there; printed, it is
// always there. Any part of it entering the screen's upper 90 % reveals it
// (a 400 % zoom shows only a sliver of a block at a time).
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';

export default function Reveal({ children, delay = 0, rise = 28, sx, component = 'div' }) {
  const ref = useRef(null);
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (reduced || !el || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setShown(true);
        io.disconnect();
      }
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);
  const visible = shown || reduced;
  return (
    <Box
      ref={ref}
      component={component}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : `translateY(${rise}px)`,
        transition: reduced ? 'none' : `opacity 700ms ${EASE} ${delay}ms, transform 700ms ${EASE} ${delay}ms`,
        '@media print': { opacity: 1, transform: 'none', transition: 'none' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
